/**
 * @author       Matthieu Devlin (matt@zumper.com)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  LocalStorage.ts
 */

import { BrowserStorage, MapStorage, NullStorage, isLocalStorageAvailable } from '@zcode/storage'
import type { History } from 'history'
import { addRecombeeToLocalStorage } from './helpers/addRecombeeToLocalStorage'
import { addTrackingToLocalStorage } from './helpers/addTrackingToLocalStorage'
import { isNativeAuth } from './helpers/isNativeAuth'
import { logger } from './logger'
import type { LocalStorage as LocalStorageType } from './types/LocalStorage'
import type { LocalStorageOptions } from './types/LocalStorageOptions'

export const defaultLocalStorageValues = {
  dislike: { listings: [], buildings: [] },
  favorites: { listings: {}, buildings: {} },
  messaged: { listings: {}, buildings: {} },
  messagedBedrooms: [],
  navbar: {},
  visited: {},
  visitedBedrooms: [],
}

export class LocalStorage implements LocalStorageType {
  readonly prefix: string
  readonly server: boolean
  readonly storage: BrowserStorage | NullStorage | MapStorage
  readonly rawStorage?: Storage
  readonly usingWindowLocalStorage: boolean

  constructor({ prefix = '', server }: LocalStorageOptions) {
    this.prefix = prefix
    this.server = server
    if (this.server) {
      this.storage = new NullStorage<unknown>()
      this.usingWindowLocalStorage = false
    } else if (isNativeAuth(window.location)) {
      this.storage = new MapStorage<unknown>({
        name: `ls-${prefix}`,
      })
      this.usingWindowLocalStorage = false
    } else {
      this.storage = new BrowserStorage<unknown>({
        // add "ls" to allow backwards compatibility,
        // i.e. old values will not have "ls" in prefix
        name: `ls-${prefix}`,
        maxSize: 100,
      })
      const browserStorage = this.storage
      // get the window.localStorage object so we can use for backwards compatibility
      // @ts-expect-error
      this.rawStorage = browserStorage.storage?.storage?.storage
      this.usingWindowLocalStorage = isLocalStorageAvailable()
    }
  }

  legacyKey(name: string): string {
    return `${this.prefix}${name}`
  }

  init(values: object = defaultLocalStorageValues) {
    for (const [name, value] of Object.entries(values)) {
      try {
        if (!this.get(name) && value !== undefined) {
          this.storage.setItem(name, value)
        }
      } catch (e) {
        logger.warn('localStorage: failed at init()', name, value)
      }
    }
  }

  get(name: string): any {
    try {
      let result = this.storage.getItem(name)
      if (result) {
        return result
      }

      // check for legacy value
      if (this.usingWindowLocalStorage && this.rawStorage) {
        result = this.rawStorage.getItem(this.legacyKey(name))
        if (result !== undefined && result !== null) {
          const legacyResult = JSON.parse(result)
          // resave using @zcode/storage - but don't delete in case we back out
          this.set(name, legacyResult)
          return legacyResult
        }
      }
    } catch (e) {
      logger.warn('localStorage: failed at get()', name, e)
    }
  }

  getx(name: string, maxLegacyTtlDays = 365) {
    try {
      let result = this.storage.getItem(name)
      if (result) {
        return result
      }

      // check for legacy value
      if (this.usingWindowLocalStorage && this.rawStorage) {
        let pair
        pair = this.rawStorage[this.legacyKey(name)]
        if (pair !== undefined && pair !== null) {
          pair = JSON.parse(pair)
          const expiresOn =
            (pair[0] as number) +
            Math.min(pair[2] || maxLegacyTtlDays, maxLegacyTtlDays) * 1000 * 24 * 60 * 60
          const ttl = expiresOn - Date.now()
          if (ttl > 0) {
            result = pair[1]
            // resave using @zcode/storage - but don't delete in case we back out
            this.setx(name, result, ttl / 1000 / 24 / 60 / 60)
            return result
          }
        }
      }
    } catch (e) {
      logger.warn('localStorage: failed at getx()', name, e)
    }
  }

  set(name: string, value: unknown) {
    try {
      logger.debug('set()', name, value)
      if (value !== undefined) {
        this.storage.setItem(name, value)
      }
    } catch (e) {
      logger.warn('localStorage: failed at set()', name, value, e)
    }
  }

  removeItem(name: string) {
    try {
      this.storage.removeItem(name)
      // delete legacy keys
      if (this.usingWindowLocalStorage && this.rawStorage) {
        this.rawStorage.removeItem(this.legacyKey(name))
      }
    } catch (e) {
      logger.warn('localStorage: failed at del()', name, e)
    }
  }

  del(name: string) {
    this.removeItem(name)
  }

  setItem(name: string, value: unknown, ttl?: number) {
    try {
      this.storage.setItem(name, value, ttl)
    } catch (e) {
      logger.warn('localStorage: failed at setItem()', name, value, e)
    }
  }

  /**
   * set variable in localstorage or (cookies if no localstorage)
   *
   * @param {string} name       String key name
   * @param {object} value      value, must be json-able.
   * @param {number} expires    ttl in days, default 365
   */
  setx(name: string, value: unknown, expires = 365) {
    logger.debug('setx()', name, value, expires)
    try {
      if (value === undefined) {
        this.del(name)
        return
      }
      // Only BrowserStorage supports ttl, NullStorage does not. NullStorage setItem is a no-op so ttl does not matter
      if (this.storage instanceof BrowserStorage) {
        // convert days to ms
        const ttl = expires * 24 * 60 * 60 * 1000
        this.storage.setItem(name, value, ttl)
      } else {
        this.storage.setItem(name, value)
      }
    } catch (e) {
      logger.warn('localStorage: failed at set()', name, value, e)
    }
  }

  clearUserInfo() {
    try {
      this.storage.clear()
      this.init()
    } catch (e) {
      logger.warn('localStorage: failed at clearUserInfo()', e)
    }
  }

  clear() {
    try {
      this.storage.clear()
    } catch (e) {
      logger.warn('localStorage: failed at clear()', e)
    }
  }

  // legacy helpers
  addTrackingToLocalStorage(history: History<unknown>) {
    return addTrackingToLocalStorage(this, history)
  }

  addRecombeeToLocalStorage(recommId: string) {
    return addRecombeeToLocalStorage(this, recommId)
  }
}
